import {
  createApiRef,
  DiscoveryApi,
  FetchApi,
} from '@backstage/core-plugin-api';
import { ApprovalRequest, ApprovalStatus } from '../types';

export const approvalApiRef = createApiRef<ApprovalApi>({
  id: 'plugin.scaffolder-approval',
});

export interface ApprovalApi {
  listRequests(options?: { status?: ApprovalStatus }): Promise<ApprovalRequest[]>;
  getRequest(id: string): Promise<ApprovalRequest>;
  approve(id: string): Promise<ApprovalRequest>;
  reject(id: string, reason?: string): Promise<ApprovalRequest>;
}

export class ApprovalClient implements ApprovalApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  private async getBaseUrl(): Promise<string> {
    return `${await this.discoveryApi.getBaseUrl('scaffolder-approval')}`;
  }

  async listRequests(
    options?: { status?: ApprovalStatus },
  ): Promise<ApprovalRequest[]> {
    const baseUrl = await this.getBaseUrl();
    const params = new URLSearchParams();
    if (options?.status) {
      params.set('status', options.status);
    }
    const query = params.toString();
    const url = `${baseUrl}/requests${query ? `?${query}` : ''}`;

    const response = await this.fetchApi.fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to list approval requests: ${response.statusText}`);
    }
    return response.json();
  }

  async getRequest(id: string): Promise<ApprovalRequest> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/requests/${id}`);
    if (!response.ok) {
      throw new Error(`Failed to get approval request: ${response.statusText}`);
    }
    return response.json();
  }

  async approve(id: string): Promise<ApprovalRequest> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/requests/${id}/approve`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      },
    );
    if (!response.ok) {
      const body = await response.text();
      throw new Error(`Failed to approve request: ${body}`);
    }
    return response.json();
  }

  async reject(id: string, reason?: string): Promise<ApprovalRequest> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/requests/${id}/reject`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason }),
      },
    );
    if (!response.ok) {
      const body = await response.text();
      throw new Error(`Failed to reject request: ${body}`);
    }
    return response.json();
  }
}
